'use client';

import * as React from 'react';
import { useSession } from '@lib/auth/client';
import { useRouter } from 'next/navigation';
import { usePmOnboardingSessionContext } from '@components/pm-onboarding-session-provider';
import { routes } from '@routes';
import { accountSupportedCountries } from '@utils/constants';
import { waitForSession } from '@utils/wait-for-session';
import { GQL_TO_STEP_KEY, OnboardingStepKey, STEP_ORDER, STEP_ROUTES } from '@utils/onboarding-steps';
import { PmOnboardingSessionStatus, PmOnboardingStepKey } from '@gql';
interface UseOnboardingStepGuardOptions {
  /** The step this page represents */
  currentStep: OnboardingStepKey;
  /** Whether this step requires authentication (default: true for all except 'join') */
  requiresAuth?: boolean;
}
interface UseOnboardingStepGuardResult {
  /** Whether the guard is still checking (loading auth or session) */
  isLoading: boolean;
  /** Whether access to this step is allowed */
  isAllowed: boolean;
  /** The onboarding session (if available) */
  session: ReturnType<typeof usePmOnboardingSessionContext>['session'];
  /** Session context methods */
  sessionContext: ReturnType<typeof usePmOnboardingSessionContext>;
}

/**
 * Hook to guard onboarding step pages.
 * Handles authentication checks and step-based navigation.
 *
 * @example
 * ```tsx
 * export default function ReviewPage() {
 *   const { isLoading, isAllowed, sessionContext } = useOnboardingStepGuard({
 *     currentStep: 'review',
 *   });
 *
 *   if (isLoading || !isAllowed) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   return <ReviewContent />;
 * }
 * ```
 */
export function useOnboardingStepGuard({
  currentStep,
  requiresAuth = currentStep !== 'join'
}: UseOnboardingStepGuardOptions): UseOnboardingStepGuardResult {
  const router = useRouter();
  const {
    status: authStatus,
    update
  } = useSession();
  const sessionContext = usePmOnboardingSessionContext();
  const {
    session: onboardingSession,
    loading: sessionLoading
  } = sessionContext;
  const [isAllowed, setIsAllowed] = React.useState(false);
  const [isRecoveringAuth, setIsRecoveringAuth] = React.useState(false);
  const hasAttemptedAuthRecoveryRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Determine if we're still loading
  const isLoading = authStatus === 'loading' || sessionLoading || isRecoveringAuth;
  React.useEffect(() => {
    if (authStatus === 'loading' || sessionLoading || isRecoveringAuth) {
      return undefined;
    }

    // Check authentication
    if (requiresAuth && authStatus === 'unauthenticated') {
      if (hasAttemptedAuthRecoveryRef.current) {
        const callbackUrl = STEP_ROUTES[currentStep];
        router.replace(`${routes.login}?callbackUrl=${encodeURIComponent(callbackUrl)}`);
        return undefined;
      }
      hasAttemptedAuthRecoveryRef.current = true;
      setIsRecoveringAuth(true);
      void (async () => {
        try {
          await waitForSession({
            interval: 100,
            timeout: 2000
          });
          await update();
          await sessionContext.refetch();
        } catch {
          // Still unauthenticated, so the next run redirects to login.
        } finally {
          if (isMountedRef.current) {
            setIsRecoveringAuth(false);
          }
        }
      })();
      return undefined;
    }

    // Do not redirect to /a/listings based on PmOnboardingSessionStatus.Completed
    // alone. The account layout uses account.onboardingCompletedAt; if that is
    // still null we would redirect here and the server would redirect back,
    // causing a loop. Let step-5 and the navbar (using onboardingCompletedAt)
    // control access to /a/listings.

    // Ineligible sessions (unsupported country or PMS) are redirected to the
    // not-supported page, which reads reviewReason off the session to tailor
    // the copy. This fires on ALL steps including join — if the backend has
    // already decided the session is ineligible, there's nothing for the user
    // to do on the join form.
    if (onboardingSession?.status === PmOnboardingSessionStatus.Ineligible) {
      router.replace(routes.onboardingNotSupported);
      return;
    }

    // Legacy fallback: if a session predates the backend's INELIGIBLE status
    // but the account country isn't supported, still redirect. Drop once all
    // sessions have been re-evaluated by the backend.
    const accountCountry = onboardingSession?.account?.countryOrRegionLongName;
    const isSupportedCountry = accountCountry ? accountSupportedCountries.includes(accountCountry) : onboardingSession?.account?.isUsBased === true;
    if (currentStep !== 'join' && onboardingSession && !isSupportedCountry) {
      router.replace(routes.onboardingNotSupported);
      return;
    }

    // Completed sessions should stay on review until onboardingCompletedAt
    // propagates; letting earlier steps render would reopen stale pages.
    if (onboardingSession?.status === PmOnboardingSessionStatus.Completed) {
      if (currentStep !== 'review') {
        router.replace(STEP_ROUTES.review);
        return undefined;
      }
    } else if (onboardingSession?.status === PmOnboardingSessionStatus.InProgress) {
      const actualStep = onboardingSession.currentStepKey;
      const normalizedActualStep = GQL_TO_STEP_KEY[actualStep as PmOnboardingStepKey];
      const actualStepIndex = normalizedActualStep ? STEP_ORDER.indexOf(normalizedActualStep) : -1;
      if (actualStepIndex === -1) {
        console.warn(`[useOnboardingStepGuard] Unknown step key from session: "${actualStep}"`);
      } else {
        const currentStepIndex = STEP_ORDER.indexOf(currentStep);
        if (currentStepIndex !== actualStepIndex) {
          const targetRoute = STEP_ROUTES[normalizedActualStep];
          if (targetRoute) {
            router.replace(targetRoute);
            return;
          }
        }
      }
    }

    // If no session exists and user is authenticated, redirect to join step
    // to start onboarding properly
    if (!onboardingSession && currentStep !== 'join' && authStatus === 'authenticated') {
      router.replace(routes.onboardingJoin);
      return;
    }

    // All checks passed
    setIsAllowed(true);
    return undefined;
  }, [authStatus, sessionLoading, isRecoveringAuth, requiresAuth, onboardingSession, currentStep, router, sessionContext, update]);
  return {
    isLoading,
    isAllowed,
    session: onboardingSession,
    sessionContext
  };
}